<template>
	<v-list-item class="px-3" :class="{
						'error': position.price === null,
						'lighten-3': position.price === null,
					}">
		<v-list-item-content class="px-0">
							<span class="overline primary--text my-0 line-height-1 font-weight-regular">
									{{ $t('frameAssembly.singular') }}
							</span>
			<v-list-item-title class="d-flex flex-column mb-2 item-pos" style="cursor: pointer" @click="showItem(position)">
				{{ itemTitle(position.item) }}
			</v-list-item-title>
			<div class="d-flex flex-row align-center gap-05 mb-2 flex-wrap">
				<ItemChipNumber x-small :item="position.item" />
				<ItemChipAltNumber v-if="position.item.alternativeItemNumber !== null" x-small :item="position.item" />
			</div>
			<v-expansion-panels @click.stop class="p-0" flat>
				<v-expansion-panel @click.stop class="px-0">
					<v-expansion-panel-header @click.stop :expand-icon="icons.dropdown" class="text-left pb-4 pt-2 px-0">
						{{ $t('frameAssembly.showConfigurations') }}
					</v-expansion-panel-header>
					<v-expansion-panel-content @click.stop class="px-0 py-0">
						<template v-for="(group, i) in position.frameAssembly?.frameAssemblyConfigurationGroups">
							<v-list-item :key="`${group.id}-entry`" class="px-0 pb-1" :class="{
													'error': position.price === null,
													'lighten-3': position.price === null,
													}">
								<v-list-item-content class="pl-4 py-1">
									<div class="mb-2 frame-type-tag text-center grey lighten-4">
										{{ `${frameTypeLabel(group.frameType)}` }}
									</div>
									<v-list-item-title v-if="group.sealPrice != null" class="d-flex flex-row justify-space-between">
															<span>
																{{ $t('frameAssembly.sealCosts') }}
															</span>
									</v-list-item-title>
									<v-list-item-subtitle v-if="group.sealPrice != null"  class="pb-2 d-flex flex-row justify-space-between">
										<span>
													{{
												$n((group.sealPrice.amount), {
													key: 'currencyDisplay',
													currency: group.sealPrice.currency.currencyCode
												}) + ` / ${$t('constants.unitPiece')}`
											}}
										</span>
										<span>
															{{
												$n((group.sealPrice.amount) * getTotalQuantityInConfigurationGroup(group), {
													key: 'currencyDisplay',
													currency: group.sealPrice.currency.currencyCode
												})
											}}
										</span>
									</v-list-item-subtitle>
									<template v-for="(configuration, i) in group.frameAssemblyConfigurations">
										<v-list-item :key="`${configuration.id}-entry`" class="pa-0" :class="{
																'error': position.price === null,
																'lighten-3': position.price === null,
																}">
											<v-list-item-content class="d-flex flex-row py-2">
												<v-list-item-title class="d-flex flex-row align-center">
																		<span>
																			{{ `${configuration.width} x ${configuration.height}`  }}
																		</span>
												</v-list-item-title>
												<v-list-item-subtitle>
																		<span>
																			{{
																				$n((position.frameAssembly.frameMeterPrice.amount), {
																					key: 'currencyDisplay',
																					currency: position.frameAssembly.frameMeterPrice.currency.currencyCode
																				}) + ` / ${$t('constants.unitMeter')}`
																			}}
																		</span>
												</v-list-item-subtitle>
												<v-list-item-subtitle class="d-flex flex-row justify-space-between align-center">
													<div class="d-flex flex-row align-center gap-1">
														<ItemQuantitySelection :value="configuration.quantity"
														                       :unit="itemSalesUnitOfMeasure(position)"
														                       x-small
														                       @click.stop
														                       @input="updateFrameAssemblyConfigurationQuantity(position, group, configuration, $event)"
														                       :default-value=1
														                       is-cart-selector/>
														<FrameAssemblyUpdateButton :frame-assembly-configuration-group="group"
														                           :frame-assembly-configuration="configuration"
														                           :position-id="position.id" @save="reloadCart"/>
														<AsiBtn :icon="icons.delete" x-small @click.stop="removeFrameAssemblyConfiguration(position, group, configuration)"/>
													</div>
													<span v-if="configuration.price !== null">
																		{{
															$n((configuration.price.amount), {
																key: 'currencyDisplay',
																currency: configuration.price.currency.currencyCode
															})
														}}
																		</span>
												</v-list-item-subtitle>
												<v-list-item-subtitle v-if="showCustomerReferences">
													<CartFrameAssemblyPositionNrInput :position-id="position.id" :configuration-group-id="group.id" :configuration="configuration" class="mt-1"/>
												</v-list-item-subtitle>
											</v-list-item-content>
										</v-list-item>
									</template>
								</v-list-item-content>
							</v-list-item>
						</template>

						<v-list-item class="pa-0">
							<v-list-item-content class="d-flex flex-row pl-4 py-0">
								<v-list-item-subtitle v-if="position.frameAssembly != null && position.frameAssembly.setupPrice != null" class="d-flex flex-row justify-space-between mb-1 mt-2">
									{{ $t('frameAssembly.setupCosts') }}
									<span class="text-right">
															{{
											$n((position.frameAssembly.setupPrice.amount), {
												key: 'currencyDisplay',
												currency: position.frameAssembly.setupPrice.currency.currencyCode
											})
										}}
									</span>
								</v-list-item-subtitle>
							</v-list-item-content>
						</v-list-item>

					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>
			<v-list-item-subtitle class="d-flex flex-row justify-space-between align-center">
				<div class="d-flex flex-row align-center">
					<ItemQuantitySelection :value="position.quantity"
					                       :unit="itemSalesUnitOfMeasure(position)"
					                       x-small class="mr-3"
					                       disabled
					                       debounce
					                       is-cart-selector
					                       :default-value=1 />
					<AsiBtn :icon="icons.delete" x-small @click.stop="removePosition(position)"/>
				</div>
				<span v-if="position.price !== null">
									{{
						$n((finalAmount(position.price)), {
							key: 'currencyDisplay',
							currency: position.price.currency.currencyCode
						})
					}}
				</span>
				<span v-else> {{ $t('cart.terms.itemUnavailable') }} </span>
			</v-list-item-subtitle>
			<v-list-item-subtitle v-if="showCustomerReferences">
				<CartPositionCustomerReferenceInput :position="position" class="mt-2"/>
			</v-list-item-subtitle>
		</v-list-item-content>
	</v-list-item>
</template>

<script lang="ts">
	import {Vue, Component, Prop} from 'vue-property-decorator';
	import ItemChipAltNumber from "@/components/item/ItemChipAltNumber.vue";
	import CartPositionCustomerReferenceInput from "@/components/cart-position/CartPositionCustomerReferenceInput.vue";
	import FrameAssemblyUpdateButton from "@/components/frame-assembly/FrameAssemblyUpdateButton.vue";
	import AsiBtn from "@/components/common/AsiBtn.vue";
	import ItemChipNumber from "@/components/item/ItemChipNumber.vue";
	import ItemQuantitySelection from "@/components/item/shop/ItemQuantitySelection.vue";
	import {ICartPositionShopSimple} from "@/models/cart-position/CartPositionShopModels";
	import Icon from "@/plugins/icons";
	import {IPrice} from "@/models/price/PriceModels";
	import TotalHelper from "@/helpers/TotalHelper";
	import Snackbar from "@/helpers/Snackbar";
	import {FrameAssemblyType, UnitOfMeasure} from "@/helpers/constants";
	import {IItemShopSimple} from "@/models/item/ItemShopModels";
	import {TranslateResult} from "vue-i18n";
	import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
	import ItemHelper from "@/models/item/ItemHelper";
	import {
		IFrameAssemblyConfiguration,
		IFrameAssemblyConfigurationGroup
	} from "@/models/frame-assembly/FrameAssemblyModels";
	import EnumHelper from "@/helpers/EnumHelper";
	import {ICartShopListEntry} from "@/models/cart/CartShopModels";
	import CartFrameAssemblyPositionNrInput
		from "@/components/cart-position/CartFrameAssemblyPositionNrInput.vue";

	@Component({
		components: {
			CartFrameAssemblyPositionNrInput,
			CartFrameAssemblyCustomerReferenceInput: CartFrameAssemblyPositionNrInput,
			ItemQuantitySelection,
			ItemChipNumber,
			AsiBtn,
			FrameAssemblyUpdateButton,
			CartPositionCustomerReferenceInput,
			ItemChipAltNumber
		}
	})
	export default class CartPositionFrameAssemblyEntry extends Vue {

		@Prop({required: true})
		public position!: ICartPositionShopSimple;

		@Prop({type: Boolean, default: false})
		public showCustomerReferences!: boolean;

		private icons = Icon;

		private finalAmount(price: IPrice): number {
			return price.amount + (this.amountsInclVat ? TotalHelper.vatAmount(price) : 0);
		}

		private get amountsInclVat(): boolean {
			return this.$store.state.ui.amountsInclVat;
		}

		private removePosition(position: ICartPositionShopSimple): void {
			this.$store.dispatch('cart/removePosition', position.id).catch(() => Snackbar.deleteError());
		}

		private removeFrameAssemblyConfiguration(position: ICartPositionShopSimple, configurationGroup: IFrameAssemblyConfigurationGroup, configuration: IFrameAssemblyConfiguration): void {
			this.$store.dispatch('cart/removeFrameAssemblyConfiguration', {
				positionId: position.id,
				configurationGroupId: configurationGroup.id,
				configurationId: configuration.id,
			}).catch(() => Snackbar.deleteError());
		}

		private updateFrameAssemblyConfigurationQuantity(position: ICartPositionShopSimple, configurationGroup: IFrameAssemblyConfigurationGroup, configuration: IFrameAssemblyConfiguration, quantity: number): void {
			if (quantity === configuration.quantity) return;
			this.$store.dispatch('cart/updateFrameAssemblyConfigurationQuantity', {
				positionId: position.id,
				configurationGroupId: configurationGroup.id,
				configurationId: configuration.id,
				quantity: quantity,
			}).catch(() => Snackbar.updateError());
		}

		private getTotalQuantityInConfigurationGroup(group: IFrameAssemblyConfigurationGroup): number{
			return group.frameAssemblyConfigurations.reduce((acc: number, cur: IFrameAssemblyConfiguration) => {
				return acc += cur.quantity;
			}, 0);
		}

		private itemSalesUnitOfMeasure(position: ICartPositionShopSimple): UnitOfMeasure | null
		{
			return position.frameAssembly !== null ? UnitOfMeasure.unitPiece : position.item.salesUnitOfMeasure;
		}

		private frameTypeLabel(frameType: FrameAssemblyType): string | null {
			return EnumHelper.textFromValue(FrameAssemblyType, frameType, true);
		}

		private itemTitle(item: IItemShopSimple): string | TranslateResult | null {
			return TranslatedValueHelper.get(item.name, this.$i18n.locale);
		}

		private showItem(position: ICartPositionShopSimple): void {
			if (this.$router.resolve(ItemHelper.detailRoute(position.item.itemNumber)).route.path === this.$route.path) return;
			this.$router.push(ItemHelper.detailRoute(position.item.itemNumber));
		}

		private reloadCart(): void {
			this.$store.dispatch('cart/loadCart', this.activeCart?.id);
		}

		private get activeCart() {
			return this.$store.getters['cart/activeCart'] as ICartShopListEntry | null;
		}
	}
</script>


<style scoped lang="scss">
	::v-deep .v-expansion-panel-content .v-expansion-panel-content__wrap {
		padding-right: 0;
	}

	::v-deep .v-expansion-panel .v-expansion-panel-header {
		min-height: fit-content;
	}

	.gap-05{
		gap: 0.5rem;
	}

	.frame-type-tag{
		font-size: 0.75rem;
		padding: 0.25rem 0.5rem;
		border-radius: 0.3rem;
	}

	.line-height-1{
		line-height: 1.5;
	}
</style>
